import { lazy, Suspense, useCallback, useEffect, useMemo } from 'react'

import { AIPanelError } from '@/features/ai/components/panel/ai-panel-error'
import { AIRailPanelSkeleton } from '@/features/ai/components/panel/ai-rail-panel-skeleton'
import { useCanUseAI } from '@/features/ai/hooks/use-can-use-ai'
import { useTenantSwitchEffect } from '@/features/ai/hooks/use-tenant-switch-effect'
import { useAIActions, useIsOpen } from '@/features/ai/store'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/utils/ui/styles'

import { ErrorBoundary } from '@/components/common/error-boundary'
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from '@/components/ui/resizable'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet'

import type { PageContext } from '@/features/ai/types'
import type { ComponentProps } from 'react'

interface AIRailLayoutProps extends ComponentProps<'div'> {
	/**
	 * Live page context resolved by the route. Required so the panel can
	 * surface page-aware bubbles and so the agent receives the correct
	 * pageContext on every send — the registry is no longer responsible for
	 * invoking page-context hooks (sidesteps the conditional-hook trap).
	 */
	pageContext: PageContext
	className?: string
}

// ─── Lazy panel body (P-21) ────────────────────────────────────────────────
// The heavy AI stack (ai / @ai-sdk / streamdown + transitive shiki/katex/mermaid)
// lives behind this single dynamic import, so it is fetched only when the panel
// opens — never shipped in the eager route bundle. `<AIRailPanelBody>` is the
// SOLE importer of `AIChat`/`AIPanel`; nothing else may static-import it (dual
// static+lazy of the same module → Vite 8/Rolldown TDZ crash, see GOTCHAS).
const AIRailPanelBody = lazy(
	() => import('@/features/ai/components/panel/ai-rail-panel-body'),
)

// Warm the panel-body chunk on user intent (Ask-Noah hover/focus) so it is in
// cache by the time the panel opens and the Suspense fallback rarely paints.
// The specifier MUST stay character-identical to the `lazy()` above or Rolldown
// emits two chunks and the warm-up misses.
let panelWarmed = false
export function prefetchAIPanel() {
	if (panelWarmed) return
	panelWarmed = true
	void import('@/features/ai/components/panel/ai-rail-panel-body')
}

// ─── Persisted layout ─────────────────────────────────────────────────────────

/**
 * `localStorage` key for the user's last main/panel split, keyed under the
 * project's `@bitlyft/{NAME}` namespace. Stored as `{ main, panel }` percentages.
 */
const LAYOUT_STORAGE_KEY = '@bitlyft/ai-panel-layout'

// react-resizable-panels v4 size props: bare numbers are interpreted as
// PIXELS (`minSize={40}` = 40px). Percentages MUST be passed as strings
// (`"40%"`). Persistence uses bare numbers internally but they are written
// back to the component via `defaultLayout` which expects percent numbers.
const DEFAULT_MAIN_SIZE = 70
const DEFAULT_PANEL_SIZE = 30
const PANEL_MIN_SIZE = 30
const PANEL_MAX_SIZE = 50
const MAIN_MIN_PCT = '40%'
const PANEL_MIN_PCT = `${PANEL_MIN_SIZE}%`
const PANEL_MAX_PCT = `${PANEL_MAX_SIZE}%`

const GROUP_ID = 'ai-rail-group'
const MAIN_PANEL_ID = 'ai-rail-main'
const PANEL_ID = 'ai-rail-panel'

// Registers the main content column as a scroll-restoration area. The page-level
// window never scrolls here (the layout caps height + hides overflow), so without
// this the router can only restore window scroll — i.e. nothing. Tagging the
// inner column lets the back button restore where the user was.
const MAIN_SCROLL_RESTORATION_ID = 'ai-rail-main-scroll'

// Published on `:root` so the (fixed-positioned) `AIDebugPanel` can anchor its
// right edge to the rail's left border via `calc(var(...) + 0.75rem)` regardless
// of where the panel is mounted in the DOM tree.
const RAIL_WIDTH_CSS_VAR = '--ai-rail-width-pct'

type StoredLayout = { main: number; panel: number }

function readStoredLayout(): StoredLayout | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
		if (!raw) return null
		const parsed: unknown = JSON.parse(raw)
		if (
			parsed &&
			typeof parsed === 'object' &&
			'main' in parsed &&
			'panel' in parsed &&
			typeof (parsed as { main: unknown }).main === 'number' &&
			typeof (parsed as { panel: unknown }).panel === 'number'
		) {
			// Clamp to the current bounds: a split saved under older limits (the
			// panel minimum was once 22%) would otherwise reach `defaultLayout` and
			// `--ai-rail-width-pct` out of range. `main` takes the remainder.
			const panel = Math.min(
				PANEL_MAX_SIZE,
				Math.max(PANEL_MIN_SIZE, (parsed as { panel: number }).panel),
			)
			return { main: 100 - panel, panel }
		}
	} catch {
		// corrupt entry — fall through to defaults
	}
	return null
}

function writeStoredLayout(layout: StoredLayout): void {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout))
	} catch {
		// quota exceeded / disabled storage — silent fallback to in-memory only
	}
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Per-route wrapper that hosts the AIR® AI side panel.
 *
 * Layout strategy:
 *
 * - Tablet/desktop (≥768px, `!useIsMobile`): renders a `<ResizablePanelGroup>`
 *   **regardless of `isOpen`**, with the route content always inside the same
 *   `<ResizablePanel id="ai-rail-main">`. The right-side panel + handle are
 *   conditionally rendered as siblings — toggling `isOpen` adds or removes
 *   them but leaves the main-content subtree's element type, position, and
 *   key identical. React reconciles instead of unmounting, so heavy children
 *   (charts, tables, query state) keep their state when the user toggles the
 *   AI panel.
 *
 *   The user can drag the divider to resize; the last split is persisted to
 *   `localStorage[@bitlyft/ai-panel-layout]` and restored on the next open.
 *   The layout root is `overflow-hidden` so the page-level scrollbar never
 *   appears — each `ResizablePanel` owns its own scroll container.
 *
 * - Mobile (<768px): renders the main content as a single flex column and the
 *   panel inside a full-width right-side `Sheet`. No resize affordance — too
 *   little horizontal real estate to be useful. The mobile tree is structurally
 *   distinct from the desktop tree, so crossing the breakpoint will remount
 *   the route subtree once. That's acceptable (it's a rare event); toggling
 *   `isOpen` within a single breakpoint does NOT remount.
 *
 * Crucially, only ONE `<AIPanel />` mounts at a time — the rail and the Sheet
 * are mutually exclusive at the React tree level (not just CSS-hidden), so
 * we don't end up with duplicate `useChat` hooks and transports racing each
 * other.
 */
export function AIRailLayout({
	children,
	pageContext,
	className,
	...props
}: AIRailLayoutProps) {
	const isOpen = useIsOpen()
	const isMobile = useIsMobile()
	const canUseAI = useCanUseAI(pageContext.page_type)
	const { closePanel } = useAIActions()

	useTenantSwitchEffect()

	// Defense-in-depth behind the per-surface trigger gates (each feature's
	// `AskAIButton` wrapper checks `useCanUseAI` before rendering): even if
	// `isOpen` is somehow set (e.g. tenant switch to a flag-off tenant while the
	// panel is open), the rail/sheet never mounts without `useCanUseAI`.
	const showPanel = isOpen && canUseAI

	// Read once on mount — `useMemo` keeps the values stable across re-renders.
	// Falling back to defaults when storage is unavailable or corrupt.
	const stored = useMemo<StoredLayout>(
		() =>
			readStoredLayout() ?? {
				main: DEFAULT_MAIN_SIZE,
				panel: DEFAULT_PANEL_SIZE,
			},
		[],
	)

	const defaultLayout = {
		[MAIN_PANEL_ID]: stored.main,
		[PANEL_ID]: stored.panel,
	}

	// `onLayoutChanged` (v4 API — fires after the drag settles) gives us the
	// final layout as `{ panelId: percentage }`. Persist the pair so the next
	// page load restores the split without a layout flash.
	const handleLayoutChanged = useCallback((layout: Record<string, number>) => {
		const main = layout[MAIN_PANEL_ID]
		const panel = layout[PANEL_ID]
		if (typeof main === 'number' && typeof panel === 'number') {
			writeStoredLayout({ main, panel })
			if (typeof document !== 'undefined') {
				document.documentElement.style.setProperty(
					RAIL_WIDTH_CSS_VAR,
					`${panel}%`,
				)
			}
		}
	}, [])

	// Keep `--ai-rail-width-pct` in sync with the rail's visibility so the
	// debug panel (rendered as a child of the composer, but `position: fixed`)
	// always anchors next to the rail's left edge. Mobile uses a full-width
	// `Sheet`, so the rail-width concept doesn't apply — fall back to 0%.
	useEffect(() => {
		if (typeof document === 'undefined') return
		const root = document.documentElement
		if (isMobile || !showPanel) {
			root.style.setProperty(RAIL_WIDTH_CSS_VAR, '0%')
		} else {
			root.style.setProperty(RAIL_WIDTH_CSS_VAR, `${stored.panel}%`)
		}
		return () => {
			root.style.removeProperty(RAIL_WIDTH_CSS_VAR)
		}
	}, [showPanel, isMobile, stored.panel])

	// Layout root caps height to the dashboard's `<main>` viewport and hides
	// overflow, so the page-level scrollbar never appears. Each child column
	// (main content + ai rail) owns its own scroll container.
	const rootClass = cn(
		'flex h-full max-h-[calc(100svh-(--spacing(24)))] w-full min-w-0 overflow-hidden',
		className,
	)

	// ── Mobile path: flat column + Sheet ────────────────────────────────────
	if (isMobile) {
		return (
			<div data-slot="ai-rail-layout" className={rootClass} {...props}>
				<div
					data-scroll-restoration-id={MAIN_SCROLL_RESTORATION_ID}
					className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto"
				>
					{children}
				</div>

				<Sheet
					open={showPanel}
					onOpenChange={(open) => {
						if (!open) closePanel()
					}}
				>
					<SheetContent
						side="right"
						className="w-full max-w-full p-0 sm:max-w-full"
					>
						<SheetHeader className="sr-only">
							<SheetTitle>Noah</SheetTitle>
							<SheetDescription>
								Ask Noah about your logs and investigations.
							</SheetDescription>
						</SheetHeader>
						<ErrorBoundary
							feature="ai"
							section="rail-mobile"
							fallback={({ error, reset }) => (
								<AIPanelError error={error} reset={reset} />
							)}
						>
							<Suspense fallback={<AIRailPanelSkeleton />}>
								<AIRailPanelBody pageContext={pageContext} hideCloseButton />
							</Suspense>
						</ErrorBoundary>
					</SheetContent>
				</Sheet>
			</div>
		)
	}

	// ── Tablet/desktop path: ResizablePanelGroup ALWAYS ─────────────────────
	// The PanelGroup + main Panel render on every render regardless of
	// `isOpen`. Toggling the panel only adds/removes the sibling handle +
	// right Panel, leaving the route subtree's React identity intact.
	return (
		<div data-slot="ai-rail-layout" className={rootClass} {...props}>
			<ResizablePanelGroup
				id={GROUP_ID}
				orientation="horizontal"
				defaultLayout={defaultLayout}
				onLayoutChanged={handleLayoutChanged}
				className="h-full"
			>
				<ResizablePanel
					id={MAIN_PANEL_ID}
					minSize={MAIN_MIN_PCT}
					data-scroll-restoration-id={MAIN_SCROLL_RESTORATION_ID}
					className="flex min-h-0 min-w-0 flex-col overflow-y-auto"
				>
					{children}
				</ResizablePanel>

				{showPanel && (
					<>
						<ResizableHandle withHandle />
						<ResizablePanel
							id={PANEL_ID}
							minSize={PANEL_MIN_PCT}
							maxSize={PANEL_MAX_PCT}
							className="bg-background flex min-w-0 flex-col"
						>
							<aside
								data-slot="ai-rail"
								aria-label="Noah"
								className="flex h-full min-w-0 flex-col border-l"
							>
								<ErrorBoundary
									feature="ai"
									section="rail-desktop"
									fallback={({ error, reset }) => (
										<AIPanelError error={error} reset={reset} />
									)}
								>
									<Suspense fallback={<AIRailPanelSkeleton />}>
										<AIRailPanelBody pageContext={pageContext} />
									</Suspense>
								</ErrorBoundary>
							</aside>
						</ResizablePanel>
					</>
				)}
			</ResizablePanelGroup>
		</div>
	)
}
